import { getState, type State } from "@/features/core/store";
import { singleFrame } from "@/features/core/timers";
import type { ViewportDirtyFlags, Repaint } from "@/features/viewport/repaint";
import { getViewportRenderSnapshot } from "@/features/viewport/runtime/channels";
import type { ViewportRenderSnapshot } from "@/features/viewport/types";
import type { Camera } from "three";
import { Scene, WebGLRenderer, WebGLRenderTarget } from "three";
import { setCanvasResolution } from "./helpers/screenUniforms";
import { RENDER_PASSES, type Layer, type RenderPassName } from "./Layer";
import { TraceCache } from "./trace/TraceCache";
import type { TraceFrame } from "./trace/views";

type Size = { width: number; height: number; dpr: number };

// The device pixel ratio the canvas renders at: never below 1, and capped at a phone's 3 so an
// outlier display cannot multiply the fill cost without bound. Below the cap the canvas is
// pixel-aligned with the screen; above it the browser would stretch the buffer.
const MAX_DPR = 3;
const clampDpr = () => Math.min(MAX_DPR, Math.max(1, window.devicePixelRatio));
// whether a scene has nothing visible to render
const drawsNothing = (scene: Scene): boolean => scene.children.every((child) => !child.visible);

// The demand-driven render loop: a frame per invalidation, in which the ticks run, the layers
// named dirty rebuild (every layer when all are), and the passes render in order, the trace pass
// through its cache when that has a bake to composite.
export class SceneManager {
  readonly scenes = Object.fromEntries(RENDER_PASSES.map((pass) => [pass, new Scene()])) as Record<RenderPassName, Scene>;
  readonly renderer: WebGLRenderer;
  private layers: Layer[] = [];
  // The trace pass renders through its accumulation cache, which bakes each chunk once
  // (world-anchored in 2D, against the view's depth in 3D) so neither a trace append nor, in 2D,
  // camera motion re-renders baked chunks, and which composites a single-sample bake while the
  // view moves instead of the MSAA canvas.
  private readonly traceCache = new TraceCache(() => this.invalidate("frame"));
  // the scenes holding the geometry that occludes the trace in 3D (see TRACE_OCCLUDER_LAYER)
  private readonly occluders = [this.scenes.transparent, this.scenes.foreground];

  private camera: Camera | null = null;
  // a frame is wanted; a tick or a layer update can want another mid-frame
  private frameRequested = true;
  private layersDirty: "all" | ViewportDirtyFlags | null = "all";
  private frame = singleFrame();
  private resizeObserver: ResizeObserver | null = null;
  private disposed = false;
  private ticks = new Set<() => void>();

  private size: Size = { width: 0, height: 0, dpr: 1 };

  constructor(canvas: HTMLCanvasElement) {
    const dpr = clampDpr();

    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: false,
      powerPreference: "high-performance",
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(dpr);
    // Querying a program's info logs on its first use is what blocks the main thread until the
    // driver has linked it; off in production, so the programs compiled ahead (see precompile)
    // link in the background and a first draw waits on nothing. The logs are for development.
    this.renderer.debug.checkShaderErrors = import.meta.env.DEV;

    this.size = { width: canvas.clientWidth, height: canvas.clientHeight, dpr };
    this.renderer.setSize(this.size.width, this.size.height, false);
    // the ribbons' screen-space widths need the CSS resolution before the first render
    setCanvasResolution(this.size.width, this.size.height);

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        this.setSize(width, height);
      }
    });
    this.resizeObserver.observe(canvas);
  }

  private setSize(width: number, height: number): void {
    const dpr = clampDpr();
    if (this.size.width === width && this.size.height === height && this.size.dpr === dpr) return;
    this.size = { width, height, dpr };
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    setCanvasResolution(width, height);
    this.invalidate();
  }

  private scheduleFrame(): void {
    if (!this.disposed) this.frame.request(this.loop);
  }

  private loop = (): void => {
    if (this.disposed || !this.frameRequested) return;
    this.frameRequested = false;

    for (const tick of this.ticks) {
      tick();
    }

    // the frame's inputs, read once after the ticks have moved the view
    const state = getState();
    const snapshot = getViewportRenderSnapshot();
    if (this.layersDirty) {
      const only = this.layersDirty === "all" ? null : this.layersDirty;
      this.layersDirty = null;
      for (const layer of this.layers) {
        if (only && layer.invalidationKeys.every((key) => !only[key])) continue;
        layer.update(state, snapshot);
      }
    }

    if (this.camera) {
      this.renderScenes(this.camera, state, snapshot);
    }

    if (this.frameRequested) {
      this.scheduleFrame();
    }
  };

  // Asks for a frame; the first one starts the demand-driven loop.
  invalidate(repaint: Repaint = "all"): void {
    if (repaint === "all") {
      this.layersDirty = "all";
      // a whole rebuild (e.g. a layer-content change) must drop the bake
      this.traceCache.markContentDirty();
    } else if (repaint !== "frame" && this.layersDirty !== "all") {
      // trace appends/evictions are detected by TraceCache itself via the
      // chunk sequence numbers, so trace/iterate dirt needs no cache flush
      this.layersDirty = { ...this.layersDirty, ...repaint };
    }
    this.frameRequested = true;
    this.scheduleFrame();
  }

  setCamera(cam: Camera): void {
    this.camera = cam;
  }

  addLayer(layer: Layer): void {
    this.layers.push(layer);
    for (const { object3D, pass } of layer.placements()) {
      this.scenes[pass].add(object3D);
    }
    this.invalidate();
  }

  // Compile every program ahead of its first draw: each pass's layers for the canvas, the trace and
  // its occluders for the cache's targets (a render target compiles its own variant), and the
  // cache's composites. Without it the first solve, the first 3D frame and the first trace each
  // stall on the shader compiler, for hundreds of milliseconds on a phone; issued at boot, the
  // compiles overlap the app's load instead.
  precompile(camera: Camera): void {
    const { renderer } = this;
    const compile = (scene: Scene) => void renderer.compile(scene, camera);
    for (const scene of Object.values(this.scenes)) compile(scene);
    compile(this.traceCache.scene);
    const target = new WebGLRenderTarget(1, 1);
    renderer.setRenderTarget(target);
    for (const scene of [this.scenes.trace, ...this.occluders]) compile(scene);
    renderer.setRenderTarget(null);
    target.dispose();
  }

  addTick(fn: () => void): void {
    this.ticks.add(fn);
  }

  removeTick(fn: () => void): void {
    this.ticks.delete(fn);
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;

    this.frame.cancel();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    for (const layer of this.layers) {
      layer.dispose();
    }
    this.layers = [];
    this.traceCache.dispose();
    for (const scene of Object.values(this.scenes)) {
      scene.clear();
    }

    this.renderer.dispose();
  }

  private renderScenes(camera: Camera, state: State, snapshot: ViewportRenderSnapshot): void {
    this.renderer.clear();
    this.renderer.autoClear = false;
    // The trace cache bakes offscreen (cache rebuild, 3D depth pre-pass) before any canvas pass
    // renders, or a mid-loop render-target switch would drop the composited result.
    const frame: TraceFrame = { renderer: this.renderer, camera, state, snapshot, trace: this.scenes.trace, occluders: this.occluders };
    const composite = this.traceCache.prepare(frame);
    for (const pass of RENDER_PASSES) {
      const scene = (pass === "trace" && composite) || this.scenes[pass];
      if (drawsNothing(scene)) continue;
      this.renderer.render(scene, camera);
    }
    this.renderer.autoClear = true;
  }
}
